import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  CopyToClipboardButton,
  FontIcon,
  IconButton,
  IDragDropEvents,
  Link,
  mergeStyles,
  mergeStyleSets,
  ShimmeredDetailsListProps,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { dangerButtonStyles } from "@components/buttons/DangerButton";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  DeploymentRingFieldDevice,
  DeploymentRingFieldDeviceDetail,
  FieldDevice
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useUpdateFieldDeviceDeploymentRing } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { FieldDeviceDeploymentRingTableRow } from "./FieldDeviceDeploymentRingTableRow";
import { useFeedbackContext } from "../../../../../libs/stores/feedback-context/feedback-context";

interface FieldDeviceDeploymentRingTableProps
  extends ShimmeredDetailsListProps {
  device: FieldDevice;
  items: DeploymentRingFieldDevice[];
  onRemoveItem: (
    fieldDeviceDeploymentRingDetail: DeploymentRingFieldDevice
  ) => void;
}

export const FieldDeviceDeploymentRingTable: React.FC<FieldDeviceDeploymentRingTableProps> = ({
  onRemoveItem,
  device,
  ...props
}) => {
  const theme = useTheme();
  const {
    mutateAsync: updateDeviceDeploymentRing
  } = useUpdateFieldDeviceDeploymentRing(device.id);

  const navigate = useNavigate();
  const { success, error } = useFeedbackContext();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const draggedItem = useRef<DeploymentRingFieldDevice | undefined>(undefined);

  const draggedIndex = useRef<number>(-1);
  const dragEnterClass = mergeStyles({
    // override both the background and the color otherwise the default styles are applied
    background: theme.palette.neutralLight,
    backgroundColor: theme.palette.neutralLight
  });

  const insertBeforeItem = async (item: DeploymentRingFieldDevice) => {
    setIsSubmitting(true);

    try {
      const draggedItems = [draggedItem.current!];

      const insertIndex = props.items.findIndex(i => i.id === item.id);
      const updatedItems = props.items.filter(
        itm => draggedItems.map(x => x.id).indexOf(itm.id) === -1
      );

      updatedItems.splice(insertIndex, 0, ...draggedItems);

      const updatePromises = Array.from(updatedItems).map(
        async (updatedItem, index) => {
          const originalItem = props.items.find(x => x.id === updatedItem.id);

          await updateDeviceDeploymentRing({
            id: updatedItem.id,
            deploymentRingId: updatedItem.deploymentRingId,
            sortOrder: index + 1,
            eTag: originalItem!.eTag,
            fieldDeviceId: updatedItem.fieldDeviceId
          });
        }
      );

      await Promise.all(updatePromises);

      success(
        "The deployment rings have been re-ordered. The device configuration will reflect this change."
      );
    } catch (e) {
      error(
        `There was an error re-ordering deployment rings: ${e.detail ??
          e.message}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDragDropEvents = (): IDragDropEvents => {
    return {
      canDrop: () => !isSubmitting,
      canDrag: () => !isSubmitting,
      onDragEnter: () => dragEnterClass,
      onDragLeave: () => {},
      onDrop: async (draggedItem?: any) => {
        if (draggedItem) {
          await insertBeforeItem(draggedItem);
        }
      },
      onDragStart: (item?: any, itemIndex?: number) => {
        draggedItem.current = item;
        draggedIndex.current = itemIndex!;
      },
      onDragEnd: () => {
        if (isSubmitting) return;
        draggedItem.current = undefined;
        draggedIndex.current = -1;
      }
    };
  };

  const columns: DataTableColumn<DeploymentRingFieldDeviceDetail>[] = [
    {
      key: "dragDropIcon",
      name: "",
      minWidth: 40,
      maxWidth: 40,
      onRender: () => <FontIcon iconName="DragObject" />
    },
    {
      key: "sortOrder",
      name: "Order",
      minWidth: 100,
      maxWidth: 100,
      isSorted: true,
      isSortedDescending: true,
      onRender: (
        fieldDeviceDeploymentRingDetail: DeploymentRingFieldDeviceDetail
      ) => <Text>{fieldDeviceDeploymentRingDetail.sortOrder}</Text>
    },
    {
      key: "name",
      name: "Deployment Ring Name",
      minWidth: 200,
      maxWidth: 300,
      onRender: (
        fieldDeviceDeploymentRingDetail: DeploymentRingFieldDeviceDetail
      ) => (
        <Link
          onClick={() =>
            navigate(
              `/system/plt/deployment-rings/${fieldDeviceDeploymentRingDetail.deploymentRingId}`
            )
          }
        >
          {fieldDeviceDeploymentRingDetail.name}
        </Link>
      )
    },
    {
      key: "description",
      name: "Deployment Ring Description",
      minWidth: 400,
      onRender: (
        fieldDeviceDeploymentRingDetail: DeploymentRingFieldDeviceDetail
      ) => <Text>{fieldDeviceDeploymentRingDetail.description}</Text>
    },
    {
      key: "deploymentRingId",
      name: "Deployment Ring Id",
      minWidth: 300,
      maxWidth: 300,
      onRender: (
        fieldDeviceDeploymentRingDetail: DeploymentRingFieldDeviceDetail
      ) => (
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
        >
          <CopyToClipboardButton
            clipboardContent={fieldDeviceDeploymentRingDetail.deploymentRingId}
          />
          <Text>{fieldDeviceDeploymentRingDetail.deploymentRingId}</Text>
        </Stack>
      )
    },
    {
      key: "code",
      name: "Deployment Ring Code",
      minWidth: 200,
      maxWidth: 200,
      onRender: (
        fieldDeviceDeploymentRingDetail: DeploymentRingFieldDeviceDetail
      ) => <Text>{fieldDeviceDeploymentRingDetail.code}</Text>
    },
    {
      key: "isPublic",
      name: "Public?",
      minWidth: 60,
      maxWidth: 60,
      onRender: (
        fieldDeviceDeploymentRingDetail: DeploymentRingFieldDeviceDetail
      ) =>
        fieldDeviceDeploymentRingDetail.isPublic ? <>Public</> : <>Private</>
    },
    {
      key: "id",
      name: "",
      minWidth: 32,
      onRender: (
        fieldDeviceDeploymentRingDetail: DeploymentRingFieldDeviceDetail
      ) => (
        <IconButton
          disabled={!hasWritePermission || isSubmitting}
          title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
          onClick={() => onRemoveItem(fieldDeviceDeploymentRingDetail)}
          iconProps={{ iconName: "Cancel" }}
          styles={mergeStyleSets(dangerButtonStyles(theme), {
            root: {
              background: "transparent"
            }
          })}
        />
      ),
      sort: false
    }
  ];

  return (
    <DataTable
      stickyHeader
      shimmerLines={5}
      columns={columns}
      onShouldVirtualize={() => true}
      dragDropEvents={getDragDropEvents()}
      onRenderRow={row =>
        row ? <FieldDeviceDeploymentRingTableRow {...row} /> : null
      }
      {...props}
    />
  );
};
